import React from 'react';
import { PhoneNumber } from '../../types/campaign';
import ErrorMessage from '../UI/ErrorMessage';

interface PhoneNumberListProps {
  phoneNumbers: PhoneNumber[];
  selectedPhoneNumberIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  loading?: boolean;
  error?: string;
  /** When true, show all Telnyx numbers; only SMS-capable ones can be selected. */
  smsMode?: boolean;
  emptyHint?: string;
}

function isPhoneSelected(phone: PhoneNumber, selectedIds: string[]): boolean {
  if (selectedIds.includes(phone.id)) return true;
  return Boolean(phone.localPhoneNumberId && selectedIds.includes(phone.localPhoneNumberId));
}

const PhoneNumberList: React.FC<PhoneNumberListProps> = ({
  phoneNumbers,
  selectedPhoneNumberIds,
  onSelectionChange,
  loading = false,
  error,
  smsMode = false,
  emptyHint,
}) => {
  const selectablePhones = smsMode
    ? phoneNumbers.filter((p) => p.smsCapable)
    : phoneNumbers;

  const selectableIds = selectablePhones.map((p) => p.id);

  const visibleSelectedCount = phoneNumbers.filter((phone) =>
    isPhoneSelected(phone, selectedPhoneNumberIds)
  ).length;

  const allSelected =
    selectableIds.length > 0 &&
    selectablePhones.every((phone) => isPhoneSelected(phone, selectedPhoneNumberIds));
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(selectableIds);
    }
  };

  const handlePhoneNumberToggle = (phone: PhoneNumber) => {
    if (smsMode && !phone.smsCapable) return;

    if (isPhoneSelected(phone, selectedPhoneNumberIds)) {
      onSelectionChange(
        selectedPhoneNumberIds.filter(
          (id) => id !== phone.id && id !== phone.localPhoneNumberId
        )
      );
      return;
    }

    const withoutLegacy = selectedPhoneNumberIds.filter(
      (id) => id !== phone.localPhoneNumberId
    );
    onSelectionChange([...withoutLegacy.filter((id) => id !== phone.id), phone.id]);
  };

  if (loading) {
    return (
      <div className="mt-1 flex items-center">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading phone numbers...</span>
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className="mt-1 p-4 bg-muted rounded-lg border border-border space-y-2">
        <p className="text-sm text-muted-foreground">
          {emptyHint ||
            'No phone numbers loaded from Telnyx. Connect Telnyx under Admin / Settings, then open the Phone Numbers page to refresh.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <div className="flex items-center justify-between p-3 bg-muted border border-border rounded-t-lg">
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              id="select-all"
              checked={allSelected}
              ref={(input) => {
                if (input) input.indeterminate = someSelected;
              }}
              onChange={handleSelectAll}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                allSelected
                  ? 'bg-primary border-primary shadow-sm'
                  : someSelected
                    ? 'bg-primary border-primary shadow-sm'
                    : 'border-border hover:border-primary/60 bg-card'
              }`}
            >
              {allSelected && (
                <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {someSelected && !allSelected && (
                <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
              )}
            </div>
          </div>
          <span className="ml-3 text-sm font-medium text-foreground">
            {allSelected ? 'Unselect All' : 'Select All'}
          </span>
        </label>
        <span className="text-xs text-muted-foreground">
          {visibleSelectedCount} of {phoneNumbers.length} selected
        </span>
      </div>

      <div className="border border-t-0 border-border rounded-b-lg max-h-64 overflow-y-auto">
        {phoneNumbers.map((phone, index) => {
          const isSelected = isPhoneSelected(phone, selectedPhoneNumberIds);
          const isLast = index === phoneNumbers.length - 1;
          const disabled = smsMode && !phone.smsCapable;

          return (
            <label
              key={phone.id}
              className={`flex items-center p-3 transition-colors ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-accent/20'
              } ${!isLast ? 'border-b border-border' : ''} ${isSelected ? 'bg-primary/5' : 'bg-card'}`}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  id={`phone-${phone.id}`}
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => handlePhoneNumberToggle(phone)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                    isSelected
                      ? 'bg-primary border-primary shadow-sm'
                      : 'border-border hover:border-primary/60 bg-card'
                  }`}
                >
                  {isSelected && (
                    <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {phone.name || phone.number}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {phone.number} • {phone.provider}
                    </div>
                  </div>
                  {smsMode && (
                    <span
                      className={`ml-2 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        phone.smsCapable
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {phone.smsCapable ? 'SMS ready' : 'Voice only'}
                    </span>
                  )}
                  {!smsMode && phone.connectionName === 'Vapi' && (
                    <span className="ml-2 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                      Vapi
                    </span>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <ErrorMessage message={error} />
    </div>
  );
};

export default PhoneNumberList;
